import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getPendingVotes, verifyVote } from '../services/api';
import { CheckCircle, XCircle, ExternalLink } from 'lucide-react';

const SuperAdminQueuePage = () => {
  const { getToken } = useAuth();
  const [pendingVotes, setPendingVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  // Helper to call API with token
  const withToken = useCallback(async (apiCall, ...args) => {
    const token = await getToken();
    return apiCall(...args, token);
  }, [getToken]);

  const fetchPendingVotes = useCallback(async () => {
    try {
      const data = await withToken(getPendingVotes);
      setPendingVotes(data);
    } catch (err) {
      console.error('Failed to fetch pending votes:', err);
      alert('Could not load pending votes');
    } finally {
      setLoading(false);
    }
  }, [withToken]);

  useEffect(() => {
    fetchPendingVotes();
  }, [fetchPendingVotes]);

  const handleReview = async (voteId, approve) => {
    if (processingId) return;
    setProcessingId(voteId);
    try {
      await withToken(verifyVote, voteId, approve, adminNote || null);
      // Refresh list
      await fetchPendingVotes();
      setAdminNote('');
    } catch (err) {
      alert(err.message || 'Failed to review vote');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatAmount = (amountInKobo) => {
    return `₦${(amountInKobo / 100).toFixed(2)}`;
  };

  if (loading) return <div className="text-center py-10">Loading pending votes...</div>;

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-secondary mb-2">🔒 Super Admin – Pending Vote Queue</h1>
        <p className="text-text-light mb-8">Review and verify user vote submissions</p>
        
        {pendingVotes.length === 0 ? (
          <div className="bg-surface rounded-lg shadow-md p-12 text-center border-l-4 border-success">
            <p className="text-text-light text-lg">✅ No pending votes awaiting verification. Great work!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingVotes.map((vote) => (
              <div key={vote.id} className="bg-surface rounded-lg shadow-md p-6 border-l-4 border-accent hover:shadow-lg transition">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Left: Vote Details */}
                  <div className="md:col-span-2">
                    <div className="mb-4 pb-4 border-b-2 border-gray-200">
                      <p className="text-sm text-text-light font-semibold mb-1">Vote ID: {vote.id}</p>
                      <p className="text-2xl font-bold text-secondary">{vote.contest_title}</p>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <p className="text-text">
                        <span className="font-semibold">👤 Voter:</span> {vote.voter_name}
                      </p>
                      <p className="text-text">
                        <span className="font-semibold">📧 Email:</span> {vote.voter_email}
                      </p>
                      <p className="text-text">
                        <span className="font-semibold">🎯 Contestant:</span> {vote.contestant_user_id}
                      </p>
                      <p className="text-text">
                        <span className="font-semibold text-accent">💵 Amount Paid:</span> {formatAmount(vote.amount_paid)}
                      </p>
                      <p className="text-text-light text-sm">
                        <span className="font-semibold">📅 Uploaded:</span> {formatDate(vote.uploaded_at)}
                      </p>
                    </div>

                    <a
                      href={vote.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary font-semibold rounded-lg hover:bg-primary/20 transition"
                    >
                      📸 View Receipt <ExternalLink size={16} />
                    </a>
                  </div>

                  {/* Right: Review Form & Actions */}
                  <div className="md:col-span-1">
                    <div className="bg-background rounded-lg p-4 mb-4">
                      <label className="block text-text font-semibold mb-3">📝 Admin Notes</label>
                      <textarea
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                        className="w-full border-2 border-gray-300 rounded p-3 text-sm focus:border-primary focus:outline-none"
                        rows="3"
                        placeholder="Add notes..."
                      />
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() => handleReview(vote.id, true)}
                        disabled={processingId === vote.id}
                        className="w-full flex items-center justify-center gap-2 bg-success text-surface font-bold py-3 rounded-lg hover:bg-success/90 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <CheckCircle size={18} /> Approve
                      </button>
                      <button
                        onClick={() => handleReview(vote.id, false)}
                        disabled={processingId === vote.id}
                        className="w-full flex items-center justify-center gap-2 bg-error text-surface font-bold py-3 rounded-lg hover:bg-error/90 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <XCircle size={18} /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminQueuePage;